import { ColumnsType } from 'antd/es/table'
import { useParams } from 'react-router-dom'
import { useMemo } from 'react'
import { useRequestDocumentList } from '@/services/healthClaim/useRequestDocumentList'
import { RequestDocumentHistory } from '@/services/healthClaim/useRequestDocumentList/types'
import { toThaiLocaleDateDashAndTime } from '@/libs/date'
import { Space } from 'antd'
import { ButtonIcon, Dot } from './styles'
import { IMAGE_URL } from '@/constant/IMAGE'
import { useRecoilValue } from 'recoil'
import healthClaimFormAtom from '@/recoil/healthClaimForm/atom'

interface SupportingDocumentsForClaimTabHistoryHookProps {
  onModalOpen: (record: RequestDocumentHistory) => void
}

const SupportingDocumentsForClaimTabHistoryHook = ({
  onModalOpen,
}: SupportingDocumentsForClaimTabHistoryHookProps) => {
  const { claimFormId } = useParams<{ claimFormId?: string }>()
  const { data: requestDocumentList, isLoading, error } = useRequestDocumentList(claimFormId || '')
  const healthClaim = useRecoilValue(healthClaimFormAtom)

  const getStatusText = (status: string) => {
    switch (status) {
      case 'UPLOADED':
        return 'อัปโหลดเอกสารเพิ่มเติมสำเร็จ'
      case 'PENDING':
        return 'รอการอัปโหลดเอกสาร'
      case 'CANCELLED':
        return 'ยกเลิกการขอเอกสาร'
      default:
        return status
    }
  }

  const columns: ColumnsType<RequestDocumentHistory> = [
    {
      title: 'ลำดับ',
      align: 'center',
      render: (_, __, index) => index + 1,
      width: 80,
    },
    {
      title: 'เอกสารที่ขอ',
      dataIndex: 'documentDetails',
      render: (documentDetails) => (
        <div>
          {documentDetails?.map((doc: any, index: number) => (
            <div key={index}>
              <div>{doc.documentTypeName}</div>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: 'โทรศัพท์มือถือ',
      dataIndex: 'phoneNumber',
      width: 120,
    },
    {
      title: 'ส่งคำขอโดย',
      dataIndex: 'requestBy',
      width: 150,
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      width: 180,
      filters: [
        { text: 'อัปโหลดเอกสารเพิ่มเติมสำเร็จ', value: 'UPLOADED' },
        { text: 'รอการอัปโหลดเอกสาร', value: 'PENDING' },
        { text: 'ยกเลิกการขอเอกสาร', value: 'CANCELLED' },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <Space>
          <Dot status={status} />
          {getStatusText(status)}
        </Space>
      ),
    },
    {
      title: 'วันที่ขอเอกสารเพิ่มเติม',
      dataIndex: 'requestDate',
      width: 180,
      render: (date) => (date ? toThaiLocaleDateDashAndTime(date) : '-'),
    },
    {
      title: 'วันที่อัปโหลดเอกสารเพิ่มเติม',
      dataIndex: 'uploadedDate',
      width: 200,
      render: (date) => (date ? toThaiLocaleDateDashAndTime(date) : '-'),
    },
    {
      fixed: 'right',
      width: 100,
      render: (_, record) => {
        return <ButtonIcon src={IMAGE_URL.eyeIcon} onClick={() => onModalOpen(record)} />
      },
    },
  ]

  const tableData = useMemo(() => {
    const pendingRecords = healthClaim.pendingRequestDocuments ?? []
    const apiRecords = requestDocumentList ?? []

    const combineAndSort = [...pendingRecords, ...apiRecords]

    if (combineAndSort.length === 0) {
      return [] as RequestDocumentHistory[]
    }

    const toTimestamp = (value?: string) => (value ? new Date(value).getTime() : 0)

    return [...combineAndSort].sort(
      (a, b) => toTimestamp(b.requestDate) - toTimestamp(a.requestDate),
    )
  }, [requestDocumentList, healthClaim.pendingRequestDocuments])

  return {
    columns,
    tableData,
    isLoading,
    error,
    claimFormId,
  }
}

export default SupportingDocumentsForClaimTabHistoryHook
